import { useEffect, useState } from "react";
import type { VideoItem } from "@/types/video";

interface PlayerModalProps {
  item: VideoItem;
  onClose: () => void;
}

export default function PlayerModal({ item, onClose }: PlayerModalProps) {
  const [status, setStatus] = useState<"loading" | "unavailable">("loading");

  useEffect(() => {
    const timer = window.setTimeout(() => setStatus("unavailable"), 2000);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="关闭播放器"
        onClick={onClose}
        className="absolute inset-0 cursor-pointer bg-primary-950/80"
      />
      <div className="relative z-10 w-full max-w-3xl overflow-hidden rounded-lg bg-primary-950">
        <div className="relative flex w-full items-center justify-center" style={{ aspectRatio: "16 / 9" }}>
          <img
            src={item.cover}
            alt={`${item.title}播放封面`}
            className="absolute inset-0 h-full w-full object-top object-cover opacity-30 blur-sm"
          />
          <div className="relative z-10 flex flex-col items-center px-6 text-center">
            {status === "loading" ? (
              <>
                <span className="flex h-14 w-14 animate-spin items-center justify-center rounded-full border-4 border-background-100/20 border-t-accent-500" />
                <p className="mt-4 text-sm text-background-100/80">正在加载播放资源…</p>
              </>
            ) : (
              <>
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-500/20 text-accent-400">
                  <i className="ri-error-warning-line text-3xl" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-background-50">暂无可用播放资源</h3>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-background-100/70">
                  本站为影视信息浏览平台，播放功能仅为前端演示界面，暂不提供真实的视频资源加载与在线播放服务。
                </p>
              </>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="absolute right-3 top-3 z-20 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-primary-950/60 text-background-50 transition-colors hover:bg-accent-500"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-primary-800 px-4 py-3">
          <div className="min-w-0">
            <p className="line-clamp-1 font-heading text-sm font-bold text-background-50">{item.title}</p>
            <p className="text-xs text-background-100/60">
              {item.year} · {item.region} · {item.genres.join(" / ")}
            </p>
          </div>
          <span className="rounded-md bg-accent-500/15 px-3 py-1.5 text-xs font-medium text-accent-300">
            <i className="ri-information-line mr-1" />
            演示模式
          </span>
        </div>
      </div>
    </div>
  );
}